/**
 * The oldest release in the field (D37 §4.1, §4.8): the cloud's node-release floor — every release a node reported
 * and was seen running in the window, and every pinned one, oldest first. What a removal of an old shape cites; the
 * cloud refuses nothing below it. `null`: an older cloud sent no `field`, and the card says so rather than guess.
 */
import type { ReleaseFieldWire } from '@acp/api-contract/vendor.js';
import { fieldReleaseText } from '@acp/api-contract/releaseText.js';
import { Badge } from '../ui/badge.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';

export type ReleaseFieldCardProps = {
  readonly field: ReleaseFieldWire | null;
};

export function ReleaseFieldCard({ field }: ReleaseFieldCardProps) {
  return (
    <Card data-testid="release-field">
      <CardHeader>
        <CardTitle>Oldest release in the field</CardTitle>
        <CardDescription>
          {field === null
            ? 'This cloud does not report the field yet.'
            : `Releases nodes were seen running in the last ${String(field.windowDays)} days, and the pinned ones — oldest first. A removal cites the oldest; nothing is refused below it.`}
        </CardDescription>
      </CardHeader>
      {field === null ? null : (
        <CardContent className="grid gap-2">
          <p className="text-sm" data-testid="field-oldest">
            {field.oldest === null ? <span className="text-muted-foreground">Nothing in the field.</span> : <span className="font-mono">{fieldReleaseText(field.oldest)}</span>}
          </p>
          {field.releases.length > 1 ? (
            <ul className="grid gap-1 text-sm text-muted-foreground" data-testid="field-releases">
              {field.releases.slice(1).map((r) => (
                <li key={r.listHash} className="font-mono" title={r.listHash} data-testid="field-release">
                  {fieldReleaseText(r)}
                </li>
              ))}
            </ul>
          ) : null}
          {field.unreported === 0 ? null : (
            <p className="flex items-center gap-2 text-sm" data-testid="field-unreported">
              <Badge variant="warning">{field.unreported} unreported</Badge>
              <span className="text-muted-foreground">node(s) reported no release (a source build, or older than D37 wave 3): not counted.</span>
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
